import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { LinkedProvider, OAuthProvider } from '@sss/sdk';
import { sdk } from '../../lib/sdk';
import { startOAuthLink } from '../../lib/oauth';

export default function AuthProvidersTab() {
  const { t } = useTranslation();
  const [linked, setLinked] = useState<LinkedProvider[] | null>(null);
  const [busy, setBusy] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [params] = useSearchParams();
  const justLinked = params.get('linked');

  async function load() {
    try {
      const list = await sdk.oauth.providers();
      setLinked(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([sdk.oauth.providers(), sdk.config.get()]).then(
      ([list, cfg]) => {
        if (cancelled) return;
        setLinked(list);
        setProviders(cfg.auth.oauthProviders);
      },
      (err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed');
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function onUnlink(provider: OAuthProvider) {
    setError(null);
    setBusy(provider);
    try {
      await sdk.oauth.unlink(provider);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }

  async function startLink(provider: OAuthProvider) {
    setError(null);
    try {
      // The API binds the round-trip to this account server-side, then
      // returns through /auth/callback?linked=<provider>.
      await startOAuthLink(provider);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  }

  if (!linked && !error) return <p>{t('common.loading')}</p>;

  return (
    <section>
      <h3>{t('settings.authProviders.title')}</h3>
      <p style={{ color: '#666' }}>{t('settings.authProviders.description')}</p>
      {justLinked && <p style={{ color: 'green' }}>{t('settings.authProviders.linkedNow', { provider: justLinked })}</p>}
      {providers.length === 0 && <p style={{ color: '#666' }}>{t('settings.authProviders.noneConfigured')}</p>}

      <ul style={list}>
        {providers.map((provider) => {
          const link = linked?.find((p) => p.provider === provider);
          const isLinked = !!link;
          return (
            <li key={provider} style={row}>
              <div>
                <strong style={{ textTransform: 'capitalize' }}>{provider}</strong>
                <div style={{ color: '#666', fontSize: '0.85rem' }}>
                  {isLinked
                    ? t('settings.authProviders.linkedAt', { date: new Date(link.linkedAt).toLocaleDateString() })
                    : t('settings.authProviders.notLinked')}
                </div>
              </div>
              {isLinked ? (
                <button onClick={() => onUnlink(provider)} disabled={busy === provider} style={btn}>
                  {busy === provider ? t('common.loading') : t('settings.authProviders.unlink')}
                </button>
              ) : (
                <button onClick={() => startLink(provider)} style={btn}>
                  {t('settings.authProviders.link')}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {error && <p style={{ color: 'red' }}>{error}</p>}
    </section>
  );
}

const list: React.CSSProperties = { listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' };
const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', border: '1px solid #ddd', borderRadius: 4 };
const btn: React.CSSProperties = { padding: '0.5rem 1rem', cursor: 'pointer' };
